import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";

import { useSWRHooksConfig } from "@touchtech/web-hooks";

import {
  ACTIVITY_MODAL_EXPIRED_TIME,
  APP_PATH,
  CHECK_ACTIVITY_INTERVAL,
  DISABLE_USER_ACTIVITY_FOR_ROUTES,
  InstoreHttpHeaderNames,
  LocalStorageKeys,
  ROUTES,
  USER_ACTIVITY_EXPIRED_TIME,
  USER_ACTIVITY_EXPIRED_TIME_ENTRY_VIEW,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { SessionTransitionContext } from "@is/contexts/SessionTransitionContext";
import { useBasketAbandon } from "@is/hooks/basket/useBasketAbandon";
import { useConfiguration } from "@is/hooks/useConfiguration";
import { useStoreEvents } from "@is/hooks/useStoreEvents";
import {
  LocalStorageService,
  createBasketUrl,
  createConfigurationUrl,
} from "@is/services";
import { TrackingEventTypes } from "@is/services/EventTracking";
import { api } from "@is/services/api";
import { withTimeout } from "@is/utils";
import {
  clearFinishedSession,
  clearQRCheckoutSession,
} from "@is/utils/session";

import { useActivityListener } from "./useActivityListener";

type CheckUserActivityType = {
  setShowUserActivityModal: (value: boolean) => void;
  setExpiredTime: (value: number) => void;
  expiredTime: number;
};

const EMPTY_ROUTE = "";
const RESTART_AWAIT_TIMEOUT_MS = 1500;

export const useCheckUserActivity = ({
  setShowUserActivityModal,
  setExpiredTime,
  expiredTime,
}: CheckUserActivityType) => {
  const [route, setRoute] = useState("");
  const { storeEvent } = useStoreEvents();
  const swrConfig = useSWRHooksConfig();

  const { timeouts } = useConfiguration({
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
  });

  const isFullExpiredTimeZero = useMemo(() => {
    if (!timeouts) {
      return false;
    }

    return (
      parseInt(timeouts.loadingBar.minutes) === 0 &&
      parseInt(timeouts.loadingBar.seconds) === 0 &&
      parseInt(timeouts.idle.minutes) === 0 &&
      parseInt(timeouts.idle.seconds) === 0
    );
  }, [timeouts]);

  const activityModalExpiredTime = useMemo(() => {
    if (route === ROUTES.EntryView || route === EMPTY_ROUTE) {
      return 0;
    }

    if (!timeouts || isFullExpiredTimeZero) {
      return ACTIVITY_MODAL_EXPIRED_TIME;
    }

    return (
      (parseInt(timeouts.loadingBar.minutes) || 0) * 60 * 1000 +
      (parseInt(timeouts.loadingBar.seconds) || 0) * 1000
    );
  }, [isFullExpiredTimeZero, route, timeouts]);

  const userActivityExpiredTime = useMemo(() => {
    if (route === ROUTES.EntryView || route === EMPTY_ROUTE) {
      return USER_ACTIVITY_EXPIRED_TIME_ENTRY_VIEW;
    }

    if (!timeouts || isFullExpiredTimeZero) {
      return USER_ACTIVITY_EXPIRED_TIME;
    }

    return (
      (parseInt(timeouts.idle.minutes) || 0) * 60 * 1000 +
      (parseInt(timeouts.idle.seconds) || 0) * 1000 +
      activityModalExpiredTime
    );
  }, [activityModalExpiredTime, isFullExpiredTimeZero, route, timeouts]);

  const [, abandonBasket] = useBasketAbandon();

  const windowLocationPathname = window.location.pathname;

  const previousSessionId = LocalStorageService.getItem(
    LocalStorageKeys.SessionId
  );

  const {
    showPrimaryAttributeDrawer,
    showLanguageAndCurrencyModal,
    showSettingsDrawer,
    showSignUpModal,
    signUpStatusModal,
  } = useContext(ViewStateContext).content;

  const sessionTransition = useContext(SessionTransitionContext);

  const preventTimeout = useMemo(() => {
    const isEntryPage = route === ROUTES.EntryView || route === EMPTY_ROUTE;

    const isAllModalsClosedOnEntryPage =
      isEntryPage &&
      !showPrimaryAttributeDrawer &&
      !showLanguageAndCurrencyModal &&
      !showSignUpModal &&
      !signUpStatusModal.isOpen;

    return (
      DISABLE_USER_ACTIVITY_FOR_ROUTES.includes(route) ||
      isAllModalsClosedOnEntryPage
    );
  }, [
    route,
    showLanguageAndCurrencyModal,
    showPrimaryAttributeDrawer,
    showSignUpModal,
    signUpStatusModal.isOpen,
  ]);

  const setNewRoute = useCallback(
    (pathname: string) => {
      const newRoute = pathname.substring(APP_PATH.length, pathname.length);

      if (route !== newRoute) {
        setRoute(newRoute);
      }
    },
    [route]
  );

  const updateExpiredTime = useCallback(() => {
    const newExpiredTime = Date.now() + userActivityExpiredTime;
    setNewRoute(window.location.pathname);

    setExpiredTime(newExpiredTime);
  }, [setExpiredTime, setNewRoute, userActivityExpiredTime]);

  const checkForInactivity = useCallback(async () => {
    setNewRoute(window.location.pathname);

    const newExpiredTime = Date.now() + userActivityExpiredTime;

    const entryViewWithOpenedPrimaryAttributeDrawer =
      (route === ROUTES.EntryView || route === EMPTY_ROUTE) &&
      (showPrimaryAttributeDrawer ||
        showLanguageAndCurrencyModal ||
        showSignUpModal ||
        signUpStatusModal.isOpen) &&
      !showSettingsDrawer &&
      expiredTime - Date.now() < 0;

    if (expiredTime - Date.now() < activityModalExpiredTime) {
      setShowUserActivityModal(true);
    }

    if (expiredTime < Date.now() || entryViewWithOpenedPrimaryAttributeDrawer) {
      setShowUserActivityModal(false);
      setExpiredTime(newExpiredTime);
      sessionTransition?.startTransition();

      try {
        await withTimeout(abandonBasket(), RESTART_AWAIT_TIMEOUT_MS);

        const newSessionId = uuid();

        LocalStorageService.setItem(LocalStorageKeys.SessionId, newSessionId);
        api.setHeader(InstoreHttpHeaderNames.SessionId, newSessionId);
        LocalStorageService.removeItem(LocalStorageKeys.Reload);
        LocalStorageService.removeItem(LocalStorageKeys.LanguageCode);
        LocalStorageService.removeItem(LocalStorageKeys.CountryCode);
        LocalStorageService.removeItem(LocalStorageKeys.CheckoutInfo);
        LocalStorageService.removeItem(LocalStorageKeys.PhoneCountryCode);
        // This path mints its own session id instead of going through
        // `useSession.createSession`, so it clears the finished-session record
        // itself — every new session starts without one (ticket aecd58ce).
        clearFinishedSession();
        clearQRCheckoutSession();

        storeEvent({
          eventType: TrackingEventTypes.ResetSessionAutomatically,
          createdAt: new Date().toISOString(),
          data: { sessionId: previousSessionId },
          sessionId: previousSessionId,
        });

        // Clear basket cache for the old session; do not create a basket for the new one
        const basketUrl = createBasketUrl();
        await swrConfig.mutate([basketUrl, previousSessionId], undefined, {
          revalidate: false,
        });

        // Revalidate configuration for the new session
        const configurationUrl = createConfigurationUrl();
        await withTimeout(
          swrConfig.mutate(configurationUrl, undefined),
          RESTART_AWAIT_TIMEOUT_MS
        );
      } catch {
        // swallow errors so redirect always fires
      } finally {
        window.location.href = `/instore${ROUTES.EntryView}`;
      }
    }
  }, [
    abandonBasket,
    activityModalExpiredTime,
    expiredTime,
    previousSessionId,
    route,
    setExpiredTime,
    setNewRoute,
    setShowUserActivityModal,
    showLanguageAndCurrencyModal,
    showPrimaryAttributeDrawer,
    showSettingsDrawer,
    showSignUpModal,
    signUpStatusModal,
    storeEvent,
    swrConfig,
    userActivityExpiredTime,
    sessionTransition,
  ]);

  useEffect(() => {
    setRoute(windowLocationPathname);
  }, [windowLocationPathname]);

  useEffect(() => {
    if (!preventTimeout) {
      const interval = setInterval(
        () => checkForInactivity(),
        CHECK_ACTIVITY_INTERVAL
      );

      return () => clearInterval(interval);
    }
  }, [checkForInactivity, preventTimeout]);

  useActivityListener(updateExpiredTime);
};
